/**
 * The pricing-authority route table.
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `PRICING_ROUTES` in — the same shape
 * `TAX_SPINE_ROUTES`, `RULES_ROUTES` and the rest already use. **P9 mounts it;
 * this packet does not edit the router.**
 *
 * Two shapes are load-bearing:
 *
 *  - **Every route is gated on a *read* permission.** Activating a precedence
 *    version, publishing a price set, keying a manual price — all are checked on
 *    the action, never on the route. An Auditor holds the read codes and nothing
 *    else, and must be able to open every one of these screens and change none
 *    of them. A route gated on a write permission would make the audit
 *    impossible while looking like security.
 *  - **`/new` is declared before `/:id(\d+)`** everywhere, so `new` can never be
 *    parsed as an id. The `\d+` constraint makes that safe anyway; the ordering
 *    makes it obvious.
 *
 * These routes replace the four pricing routes that used to live in
 * `SALES_ROUTES` (`/sales/price-lists`, `/sales/price-lists/new`,
 * `/sales/price-lists/:id`, `/sales/promotions`). Those screens moved to this
 * module with the engine, and the sales table no longer declares them — see this
 * packet's report for the redirects P9 should add so a bookmark still lands
 * somewhere useful.
 */

import type { RouteRecordRaw } from 'vue-router'

import { PRICING_PERMISSIONS } from '@/modules/pricing/permissions'
import { PRICING_SIMULATOR_ROUTES } from '@/modules/pricing/simulator/routes'

export const PRICING_ROUTES: RouteRecordRaw[] = [
  ...PRICING_SIMULATOR_ROUTES,
  {
    path: '/pricing/price-lists',
    name: 'pricing-price-lists',
    component: () => import('@/modules/pricing/PriceListsView.vue'),
    meta: { titleKey: 'pricing.routeTitles.priceLists', permission: PRICING_PERMISSIONS.priceRead },
  },
  {
    path: '/pricing/price-lists/new',
    name: 'pricing-price-list-new',
    component: () => import('@/modules/pricing/PriceListEditor.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.priceListNew',
      permission: PRICING_PERMISSIONS.priceManage,
    },
  },
  {
    path: '/pricing/price-lists/:id(\\d+)',
    name: 'pricing-price-list',
    component: () => import('@/modules/pricing/PriceListEditor.vue'),
    meta: { titleKey: 'pricing.routeTitles.priceList', permission: PRICING_PERMISSIONS.priceRead },
  },
  {
    path: '/pricing/promotions',
    name: 'pricing-promotions',
    component: () => import('@/modules/pricing/PromotionsView.vue'),
    meta: { titleKey: 'pricing.routeTitles.promotions', permission: PRICING_PERMISSIONS.priceRead },
  },
  {
    path: '/pricing/item-settings',
    name: 'pricing-item-settings',
    component: () => import('@/modules/pricing/ItemSettingsView.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.itemSettings',
      permission: PRICING_PERMISSIONS.priceRead,
    },
  },
  {
    path: '/pricing/categories',
    name: 'pricing-categories',
    component: () => import('@/modules/pricing/CategoriesView.vue'),
    meta: { titleKey: 'pricing.routeTitles.categories', permission: PRICING_PERMISSIONS.priceRead },
  },
  {
    path: '/pricing/precedence',
    name: 'pricing-precedence',
    component: () => import('@/modules/pricing/PrecedenceVersionsView.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.precedence',
      permission: PRICING_PERMISSIONS.precedenceRead,
    },
  },
  {
    path: '/pricing/precedence/:id(\\d+)',
    name: 'pricing-precedence-version',
    component: () => import('@/modules/pricing/PrecedenceEditor.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.precedenceVersion',
      permission: PRICING_PERMISSIONS.precedenceRead,
    },
  },
  {
    path: '/pricing/price-sets',
    name: 'pricing-price-sets',
    component: () => import('@/modules/pricing/PriceSetsView.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.priceSets',
      permission: PRICING_PERMISSIONS.priceSetRead,
    },
  },
  {
    path: '/pricing/price-sets/:id(\\d+)',
    name: 'pricing-price-set',
    component: () => import('@/modules/pricing/PriceSetVersionsView.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.priceSet',
      permission: PRICING_PERMISSIONS.priceSetRead,
    },
  },
  /* P45 run 3 (U70) — FSD 5.2.3: price books, price history, price change impact. */
  {
    path: '/pricing/price-books',
    name: 'pricing-price-books',
    component: () => import('@/modules/pricing/price-books/PriceBooksView.vue'),
    meta: { titleKey: 'pricing.u70.routeTitles.priceBooks', permission: PRICING_PERMISSIONS.priceRead },
  },
  {
    path: '/pricing/history',
    name: 'pricing-history',
    component: () => import('@/modules/pricing/history/PriceHistoryView.vue'),
    meta: { titleKey: 'pricing.u70.routeTitles.history', permission: PRICING_PERMISSIONS.historyRead },
  },
  {
    path: '/pricing/impact',
    name: 'pricing-impact',
    component: () => import('@/modules/pricing/impact/PriceImpactView.vue'),
    meta: { titleKey: 'pricing.u70.routeTitles.impact', permission: PRICING_PERMISSIONS.impactRun },
  },
  {
    path: '/pricing/explanations',
    name: 'pricing-explanations',
    component: () => import('@/modules/pricing/ExplanationsView.vue'),
    meta: {
      titleKey: 'pricing.routeTitles.explanations',
      permission: PRICING_PERMISSIONS.explanationRead,
    },
  },
]
